if (typeof CartForm !== 'function') {
  class CartForm extends HTMLElement {
    constructor() {
      super();
      this.ajaxifyCartItems();
    }

    ajaxifyCartItems() {
      this.form = this.querySelector('form');

      this.querySelectorAll('[data-js-cart-item]').forEach(item => {
        // Skip if already initialized - prevents duplicate event listeners
        if (item._ajaxified) return;
        item._ajaxified = true;

        const remove = item.querySelector('.remove');
        if (remove) {
          remove.dataset.href = remove.getAttribute('href');
          remove.setAttribute('href', '');
          // Set flag on mousedown to prevent blur from firing redundant update
          remove.addEventListener('mousedown', () => {
            item._removingItem = true;
          });
          remove.addEventListener('click', e => {
            e.preventDefault();
            this.updateCartQty(item, 0);
          });
        }

        const qty = item.querySelector('.qty');
        if (qty) {
          // REQ-009: FR-003 - Update cart after 800ms of inactivity (industry standard debounce)
          // Store timeout on the item element so buttons can clear it
          item._typingTimeout = null;
          // Track last committed value to prevent redundant updates
          item._lastCommittedQty = parseInt(qty.value) || 0;

          const updateQty = () => {
            const newValue = parseInt(qty.value);
            // Only update if value changed from last committed value
            if (!isNaN(newValue) && newValue >= 0 && newValue !== item._lastCommittedQty) {
              item._lastCommittedQty = newValue;
              this.updateCartQty(item, newValue);
            }
          };

          // Update after 800ms of no typing (industry standard)
          qty.addEventListener('input', () => {
            clearTimeout(item._typingTimeout);
            item._typingTimeout = setTimeout(() => {
              updateQty();
            }, 800);
          });

          // Also update when user clicks away (but not if clicking remove button or value unchanged)
          qty.addEventListener('blur', () => {
            clearTimeout(item._typingTimeout);
            if (item._removingItem) {
              item._removingItem = false;
              return;
            }
            updateQty();
          });

          // Update immediately when user presses Enter
          qty.addEventListener('keydown', e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              clearTimeout(item._typingTimeout);
              e.target.blur(); // Trigger blur which will call updateQty
            }
          });

          // Select all text when clicking in the field for easy editing
          qty.addEventListener('click', e => {
            e.target.select();
          });
        }
      });
    }

    updateCartQty(item, newQty) {
      let alert = null;

      // Track this as the last committed qty (prevents redundant updates from any source)
      item._lastCommittedQty = newQty;

      this.form.classList.add('processing');
      const existingAlert = this.querySelector('.alert');
      if (existingAlert) {
        existingAlert.remove();
      }

      const body = JSON.stringify({
        line: parseInt(item.dataset.line),
        quantity: newQty
      });

      fetch(KROWN.settings.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body
      })
        .then(response => {
          // Third-party apps (e.g., Shopacado) intercept fetch and return boolean
          // when no change is needed (e.g., qty already at requested value)
          // In this case, skip the cart refresh entirely for efficiency
          if (typeof response !== 'object' || !response.text || response.constructor.name !== 'Response') {
            return { status: 'no-change' };
          }

          const status = response.status;

          // Try to parse response to check for errors (422 = stock limit)
          return response.text().then(text => {
            try {
              const data = JSON.parse(text);
              data.status = status;
              return data;
            } catch (_e) {
              return { status: status };
            }
          });
        })
        .then(response => {
          // No change detected by third-party app - skip cart refresh for efficiency
          if (response.status === 'no-change') {
            this.form.classList.remove('processing');
            return Promise.reject('no-change'); // Exit promise chain early
          }

          // Check for stock limit error
          if (response.status == 422) {
            alert = document.createElement('span');
            alert.classList.add('alert', 'alert--error');
            if (typeof response.description === 'string') {
              alert.innerHTML = response.description;
            } else if (response.message) {
              alert.innerHTML = response.message;
            }
          }

          // REQ-009: FR-002 - Always fetch fresh cart state with discounts
          return fetch('/cart.js');
        })
        .then(response => response.json())
        .then(cartData => {
          // REQ-009: FR-002 - Surgical DOM updates
          this.updateCartDisplay(cartData);

          if (alert !== null) {
            this.form.prepend(alert);
          }

          const event = new CustomEvent('cart-updated', { detail: { cart: cartData } });
          this.dispatchEvent(event);
        })
        .catch(e => {
          // 'no-change' is not an error - it's an early exit when cart state unchanged
          if (e === 'no-change') return;

          // eslint-disable-next-line no-console
          console.error('Cart update error:', e);
          const errorAlert = document.createElement('span');
          errorAlert.classList.add('alert', 'alert--error');
          errorAlert.textContent = KROWN.settings.locales.cart_general_error;
          this.form.prepend(errorAlert);
        })
        .finally(() => {
          this.form.classList.remove('processing');
        });
    }

    updateCartDisplay(cartData) {
      // REQ-009: FR-002 - Surgical DOM updates for better performance
      // Updates specific cart elements without full innerHTML replacement

      // 1. Handle empty cart state
      const cartItemCount = cartData.items.length;
      const cartElement = document.getElementById('cart');

      if (cartItemCount === 0) {
        // Cart is now empty - add cart--empty class to trigger MutationObserver
        if (cartElement) {
          cartElement.classList.add('cart--empty');
        }

        // Remove all line items from DOM
        this.querySelectorAll('[data-js-cart-item]').forEach(item => item.remove());

        // Add empty cart message if not already present
        const cartHolder = this.querySelector('.cart-holder');
        if (cartHolder && !cartHolder.querySelector('.cart__empty')) {
          const emptyMessage = document.createElement('p');
          emptyMessage.className = 'cart__empty';
          emptyMessage.textContent = KROWN.settings.locales.cart_empty || 'Your cart is currently empty.';
          cartHolder.appendChild(emptyMessage);
        }

        // Update header cart count and total
        document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
          elm.textContent = '0';
          // Only hide parent if it's the cart__count badge (not sidebar title)
          const parentSpan = elm.closest('.cart__count');
          if (parentSpan) {
            parentSpan.style.display = 'none';
          }
        });

        document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
          elm.textContent = this.formatMoney(0);
        });

        // Update cart subtotal in footer
        const subtotalElm = document.querySelector('#AjaxCartSubtotal .cart__total strong');
        if (subtotalElm) {
          subtotalElm.textContent = this.formatMoney(0);
        }

        // Hide cart-level discount rows (no discounts when cart empty)
        const cartSubtotalRow = document.querySelector('.js-cart-subtotal');
        const cartDiscountsRow = document.querySelector('.js-cart-discounts');
        if (cartSubtotalRow) cartSubtotalRow.style.display = 'none';
        if (cartDiscountsRow) cartDiscountsRow.style.display = 'none';

        return; // No need to update individual items
      } else {
        // Cart has items - remove cart--empty class and empty message
        if (cartElement) {
          cartElement.classList.remove('cart--empty');
        }

        // Remove empty cart message if present
        const emptyMessage = this.querySelector('.cart__empty');
        if (emptyMessage) {
          emptyMessage.remove();
        }
      }

      // 2. Remove items that no longer exist in cart (handles item removal)
      // NOTE: Assumes DOM line items are ordered to match Shopify's cart.items array
      // and data-line attributes are 1-indexed matching item position
      const allLineItems = this.querySelectorAll('[data-js-cart-item]');
      allLineItems.forEach((lineItem, index) => {
        if (index >= cartItemCount) {
          lineItem.remove();
        }
      });

      // 3. Update each line item's data and display
      cartData.items.forEach((item, index) => {
        const lineItem = this.querySelector(`[data-line="${index + 1}"]`);
        if (!lineItem) return;

        // Update data attributes to keep in sync with API
        lineItem.setAttribute('data-line', index + 1);
        lineItem.setAttribute('data-qty', item.quantity);

        // Update quantity input
        const qtyInput = lineItem.querySelector('.qty');
        if (qtyInput) {
          qtyInput.value = item.quantity;
        }

        // Update line total price
        const linePrice = lineItem.querySelector('.cart-item__price strong');
        if (linePrice) {
          linePrice.textContent = this.formatMoney(item.final_line_price);
        }

        // REQ-009: FR-002 - Update line-level discount (pre-rendered elements, just toggle visibility)
        // Elements are always present in DOM, rendered by Liquid template
        const hasLineDiscount = item.original_line_price && item.original_line_price > item.final_line_price;

        // Update line discount strikethrough (original price)
        const lineDiscountSpan = lineItem.querySelector('.js-line-discount');
        const lineSaleLabel = lineItem.querySelector('.js-sale-label');

        if (lineDiscountSpan) {
          lineDiscountSpan.style.display = hasLineDiscount ? '' : 'none';
          // Update the strikethrough price
          const delElm = lineDiscountSpan.querySelector('del');
          if (delElm && hasLineDiscount) {
            delElm.textContent = this.formatMoney(item.original_line_price);
          }
        }
        if (lineSaleLabel) {
          lineSaleLabel.style.display = hasLineDiscount ? '' : 'none';
        }

        // Update discount label text and title
        const discountLabel = lineItem.querySelector('.js-discount-label');
        const discountTitle = lineItem.querySelector('.js-discount-title');
        const discountAmount = lineItem.querySelector('.js-discount-amount');

        if (discountLabel) {
          discountLabel.style.display = hasLineDiscount ? '' : 'none';
        }
        // Update discount title from API (e.g., "Bundle & Save (Automatically Applied)")
        if (discountTitle && hasLineDiscount && item.line_level_discount_allocations?.length > 0) {
          discountTitle.textContent = item.line_level_discount_allocations[0].discount_application.title;
        }
        if (discountAmount && hasLineDiscount) {
          const savings = item.original_line_price - item.final_line_price;
          discountAmount.textContent = `-${this.formatMoney(savings)}`;
        }
      });

      // 4. Update header cart count
      document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
        elm.textContent = cartData.item_count;
        // Only show/hide the cart__count badge (not sidebar title)
        const parentSpan = elm.closest('.cart__count');
        if (parentSpan) {
          parentSpan.style.display = cartData.item_count > 0 ? '' : 'none';
        }
      });

      // 5. Update header cart total
      document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
        elm.textContent = this.formatMoney(cartData.total_price);
      });

      // 6. Update cart subtotal in footer
      const subtotalElm = document.querySelector('#AjaxCartSubtotal .cart__total strong');
      if (subtotalElm) {
        subtotalElm.textContent = this.formatMoney(cartData.total_price);
      }

      // 6b. Update cart-level discounts (subtotal before discounts, discount details)
      const hasCartDiscounts = cartData.cart_level_discount_applications?.length > 0;
      const cartSubtotalRow = document.querySelector('.js-cart-subtotal');
      const cartDiscountsRow = document.querySelector('.js-cart-discounts');

      if (cartSubtotalRow) {
        cartSubtotalRow.style.display = hasCartDiscounts ? '' : 'none';
        const itemsSubtotal = cartSubtotalRow.querySelector('.js-items-subtotal');
        if (itemsSubtotal && hasCartDiscounts) {
          itemsSubtotal.textContent = this.formatMoney(cartData.items_subtotal_price);
        }
      }

      if (cartDiscountsRow) {
        cartDiscountsRow.style.display = hasCartDiscounts ? '' : 'none';
        if (hasCartDiscounts) {
          // Update first discount item (we only have one placeholder)
          const discountTitle = cartDiscountsRow.querySelector('.js-cart-discount-title');
          const discountAmount = cartDiscountsRow.querySelector('.js-cart-discount-amount');
          const firstDiscount = cartData.cart_level_discount_applications[0];

          if (discountTitle) {
            discountTitle.textContent = firstDiscount.title;
          }
          if (discountAmount) {
            discountAmount.textContent = this.formatMoney(firstDiscount.total_allocated_amount);
          }
        }
      }

      // 7. Update shipping notice with new cart total
      const shippingNotice = document.querySelector('shipping-notice');
      if (shippingNotice) {
        shippingNotice.setAttribute('data-cart-total-amount', cartData.total_price);
        if (typeof shippingNotice.init === 'function') {
          shippingNotice.init();
        }
      }

      // 8. Update hidden cart count/total elements (used by other scripts)
      const hiddenCount = this.querySelector('[data-cart-count]');
      if (hiddenCount) {
        hiddenCount.textContent = cartData.item_count;
      }

      const hiddenTotal = this.querySelector('[data-cart-total]');
      if (hiddenTotal) {
        hiddenTotal.textContent = this.formatMoney(cartData.total_price);
      }

      // 9. Re-attach event handlers since we may have removed/added items
      this.ajaxifyCartItems();
    }

    formatMoney(cents) {
      // Use Shopify's money formatter if available (multi-currency safe)
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
      }

      // Fallback formatter
      const amount = (cents / 100).toFixed(2);
      return `$${amount}`;
    }
  }

  if (typeof customElements.get('cart-form') == 'undefined') {
    customElements.define('cart-form', CartForm);
  }
}

if (typeof CartProductQuantity !== 'function') {
  class CartProductQuantity extends HTMLElement {
    constructor() {
      super();
      const minus = this.querySelector('.qty-minus');
      const plus = this.querySelector('.qty-plus');
      if (!minus || !plus) return;

      const handler = this.changeCartInput.bind(this);
      minus.addEventListener('click', handler);
      plus.addEventListener('click', handler);
    }
    changeCartInput() {
      const item = this.closest('[data-js-cart-item]');

      // REQ-009: FR-003 - Clear any pending input typing timeout
      if (item._typingTimeout) {
        clearTimeout(item._typingTimeout);
        item._typingTimeout = null;
      }

      // REQ-009: FR-003 - Debounce button clicks (500ms after last click)
      // This allows rapid clicking without overwhelming the API
      if (item._buttonTimeout) {
        clearTimeout(item._buttonTimeout);
      }

      // Button updates happen in next tick, so qty.value is already updated by browser
      item._buttonTimeout = setTimeout(() => {
        document
          .getElementById('AjaxCartForm')
          .updateCartQty(item, parseInt(this.querySelector('.qty').value));
        item._buttonTimeout = null;
      }, 500); // Update 500ms after last button click
    }
  }

  if (typeof customElements.get('cart-product-quantity') == 'undefined') {
    customElements.define('cart-product-quantity', CartProductQuantity);
  }
}

// method for apps to tap into and refresh the cart

if (!window.refreshCart) {
  // REQ-009: FR-002 - refreshCart is called when items are ADDED to cart
  // Unlike updateCartDisplay (which does surgical updates for qty changes),
  // this needs to do a full section re-render to create new line item HTML
  window.refreshCart = () => {
    fetch('/?section_id=helper-cart')
      .then(response => response.text())
      .then(html => {
        const div = document.createElement('div');
        div.innerHTML = html;

        // Replace cart form content with fresh HTML
        const newCartForm = div.querySelector('#AjaxCartForm') || div.querySelector('cart-form');
        const oldCartForm = document.getElementById('AjaxCartForm');
        if (newCartForm && oldCartForm) {
          oldCartForm.innerHTML = newCartForm.innerHTML;
          oldCartForm.ajaxifyCartItems(); // Re-attach event handlers
        }

        // Replace cart subtotal with fresh HTML
        const newSubtotal = div.querySelector('#AjaxCartSubtotal');
        const oldSubtotal = document.querySelector('#AjaxCartSubtotal');
        if (newSubtotal && oldSubtotal) {
          oldSubtotal.innerHTML = newSubtotal.innerHTML;
        }

        // Update header cart count from hidden element in cart-form
        const newCount = div.querySelector('[data-cart-count]');
        if (newCount) {
          const count = newCount.textContent.trim();
          document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
            elm.textContent = count;
            // Only show/hide the cart__count badge (not sidebar title)
            const parentSpan = elm.closest('.cart__count');
            if (parentSpan) {
              parentSpan.style.display = parseInt(count) > 0 ? '' : 'none';
            }
          });
        }

        // Update header cart total from hidden element in cart-form
        const newTotal = div.querySelector('[data-cart-total]');
        if (newTotal) {
          document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
            elm.textContent = newTotal.textContent.trim();
          });

          // REQ-009: Update shipping notice with fresh cart total
          const totalCents = newTotal.getAttribute('data-cart-total-cents');
          if (totalCents) {
            const shippingNotice = document.querySelector('shipping-notice');
            if (shippingNotice) {
              shippingNotice.setAttribute('data-cart-total-amount', totalCents);
              if (typeof shippingNotice.init === 'function') {
                shippingNotice.init();
              }
            }
          }
        }

        // Show cart drawer
        const cartSidebar = document.querySelector('[data-js-site-cart-sidebar]');
        if (cartSidebar) {
          cartSidebar.show();
        }

        // Regenerate recommendations if present
        const cartRecs = document.querySelector('cart-recommendations');
        if (cartRecs) {
          cartRecs.innerHTML = '';
          cartRecs.generateRecommendations();
        }
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error('refreshCart error:', e);
      });
  };
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'input',
          debounce(event => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${KROWN.settings.routes.cart_update_url}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
              body
            });
          }, 150)
        );
      }
    }
  );
}
